import React, { Fragment } from "react";
import styles from "./UiLibraryComponent.module.css";
import TrashIcon from "Utils/Assets/SVGs/TrashIcon";
import UploadIcon from "Utils/Assets/SVGs/UploadIcon";
import PdfIcon from "Utils/Assets/SVGs/PdfIcon";
import { useUpload } from "../../../Logic/Functions/Hooks/useUpload";
import { UploadProps } from "@model/UIComponents.model";
import TextInput from "./TextInput";

const Upload: React.FC<UploadProps> = ({
  value,
  onChange,
  mimeTypes,
  required = false,
  maxSizeMB = 5,
  maxFiles = 1,
  maxTotalMB,
  error,
  helperText,
  showInlineError = true,
  badTypeMessage,
  existingFiles = [],
  onRemoveExisting,
  requiresLabel,
  parentTestId
}) => {
  const {
    inputRef,
    accept,
    allowMultiple,
    hasFiles,
    rows,
    primaryButtonLabel,
    primaryButtonDisabled,
    primaryButtonTitle,
    remainingMessage,
    helperMessage,
    errorMessage,
    openFilePicker,
    handleInputChange,
  } = useUpload({
    value,
    onChange,
    mimeTypes,
    required,
    maxSizeMB,
    maxFiles,
    maxTotalMB,
    badTypeMessage,
    existingFiles,
    onRemoveExisting,
    showInlineError,
    error,
    helperText,
    requiresLabel,
    parentTestId
  });

  return (
    <div className={styles.uplContainer} data-testid="fileupload-container">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={allowMultiple}
        onChange={handleInputChange}
        className={styles.uplHidden}
        data-testid="fileupload-input"
      />

      {!hasFiles ? (
        <div className={styles.uplCard} data-testid="fileupload-empty">
          <div className={styles.uplCardThumb}>
            <div className={styles.uplThumbFallback} />
          </div>

          <div className={styles.uplCardInfo}>
            <div className={styles.uplFilename}>
              No file selected{" "}
              {required && <span className={styles.uplRequiredAsterisk}>*</span>}
            </div>
            <div className={styles.uplMeta}>Select file(s) to upload</div>
          </div>

          <div className={`${styles.uplActions} ${styles.uplActionsEqual}`}>
            <button
              type="button"
              className={`${styles.uplBtn} ${styles.uplBtnPrimary}`}
              onClick={openFilePicker}
              data-testid="fileupload-primary"
            >
              <UploadIcon width={16} height={16} className={styles.uplIcon} />
              Upload
            </button>
          </div>
        </div>
      ) : (
        <Fragment>
          <ul className={styles.uplList} data-testid="fileupload-list">
            {rows.map((row, index) => (
              <li
                key={row.key}
                className={styles.uplItem}
                data-testid={row.testId}
              >
                <div className={styles.uplThumbLeft}>
                  {row.thumbnailType === "pdf" ? (
                    <PdfIcon width={34} height={34} />
                  ) : row.thumbnailType === "image" && row.thumbnailUrl ? (
                    <img src={row.thumbnailUrl} alt={row.fileName} />
                  ) : (
                    <div className={styles.uplThumbFallback} />
                  )}
                </div>

                <div className={styles.uplInfoCenter}>
                  <div className={styles.uplFilename} title={row.fileName}>
                    {row.thumbnailType === "pdf" && row.kind === "existing" && row.url ? (
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.uplLink}
                         data-testid={`fileupload-link-${index}`} 
                      >
                        {row.fileName}
                      </a>
                    ) : (
                      row.fileName
                    )}
                  </div>
                {row.kind === "new" && requiresLabel && row.onLabelChange && <div>
                  <TextInput
                        label=""
                        placeholder="File Label"
                        value={row.labelValue ?? ""}
                        error={row.labelError ?? ""}
                        onChange={(value) => row.onLabelChange!(value)}
                        onBlur={row.onLabelBlur}
                        data-testid={`fileupload-label-${index}`}
                      />
                      </div>}

                  <span className={styles.uplSizeChip}>{row.metaText}</span>
                </div>

                <div className={styles.uplRight}>
                  {row.canRemove && row.onRemove && (
                    <button
                      type="button"
                      className={styles.uplRemoveBtn}
                      onClick={row.onRemove}
                      aria-label={`Remove ${row.fileName}`}
                      title="Remove"
                      data-testid={`fileupload-remove-row-${index}`}
                    >
                      <TrashIcon width={18} height={18} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className={styles.uplFooter} data-testid="fileupload-footer">
            <div className={styles.uplFooterLeft}>
              {remainingMessage && (
                <div
                  className={styles.errorMessage}
                  data-testid="fileupload-remaining"
                >
                  {remainingMessage}
                </div>
              )}
            </div>

            <div className={styles.uplFooterRight}>
              <button
                type="button"
                className={`${styles.uplBtn} ${styles.uplBtnPrimary}`}
                onClick={openFilePicker}
                disabled={primaryButtonDisabled}
                title={primaryButtonTitle}
                data-testid="fileupload-primary"
              >
                <UploadIcon width={16} height={16} className={styles.uplIcon} />
                {primaryButtonLabel}
              </button>
            </div>
          </div>
        </Fragment>
      )}

      {helperMessage && !errorMessage && (
        <p className={styles.uplHelper} data-testid="fileupload-helper">
          {helperMessage}
        </p>
      )}

      {errorMessage && (
        <p className={styles.errorMessage} data-testid={`${parentTestId}-error`}>
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default Upload;
